<template>
  <main class="bg-gray-300 min-h-screen">
    <header class="">
      <div
        class="border-b border-gray-400 px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8"
      >
        <div class="flex-1 min-w-0">
          <h1 class="text-lg font-medium leading-6 text-gray-900 sm:truncate">
            {{ $t('models_title') }}
          </h1>
        </div>
      </div>
      <sqr-progress v-show="loading" />
      <sqr-error-banner :error="loadError" @retry="load({ reload: true })" />
    </header>

    <div class="max-w-4xl mx-auto">
      <div class="max-w-md py-10 mx-auto md:mx-0">
        <div class="p-4 bg-white rounded shadow">
          <p class="text-gray-800 mb-3">{{ $t('models_lead_1') }}</p>
          <p class="text-gray-800 mb-3">{{ $t('models_lead_2') }}</p>
          <sqr-error-text :error="addError" />
          <div class="flex justify-end">
            <sqr-button
              :label="$t('models_add')"
              @click="modelAdd({})"
              color="blue"
              :loading="adding"
            >
              <template slot="icon">
                <PlusIcon class="inline w-4 h-4 stroke-current" />
              </template>
            </sqr-button>
          </div>
        </div>
      </div>

      <section
        class="pb-6"
        v-for="(model, modelIndex) in models"
        :key="model.id"
      >
        <div class="bg-white rounded shadow">
          <div class="p-4 flex justify-between">
            <sqr-input-text
              :label="$t('model_name')"
              :value="model.name"
              @change="modelFieldSet(modelIndex, 'name', $event)"
            />
            <sqr-button
              :label="$t('buttons_remove')"
              @click="modelRemove(modelIndex)"
              color="white"
              second="danger"
              :loading="removingById[model.id]"
            >
              <template slot="icon">
                <TrashIcon class="inline w-4 h-4 stroke-current" />
              </template>
            </sqr-button>
          </div>

          <invoice-lines-form
            class="border-t"
            :lines="model.lines"
            @add="lineAdd(modelIndex)"
            @change="lineFieldSet({ ...$event, modelIndex })"
            @remove="lineRemove({ ...$event, modelIndex })"
            no-dates
            no-amounts
            no-models
          />
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import firebase from 'firebase/app';
import { remove, set, lensIndex, lensPath } from 'ramda';
import nanourl from '@/utils/nanourl';
import SqrInputText from '@/components/SqrInputText';
import SqrButton from '@/components/SqrButton';
import SqrErrorBanner from '@/components/SqrErrorBanner';
import SqrErrorText from '@/components/SqrErrorText';
import InvoiceLinesForm from '@/forms/InvoiceLinesForm';

import invoiceLineComputeByField from '@/utils/invoiceLineComputeByField';
import { PlusIcon, TrashIcon } from '@vue-hero-icons/outline';

export default {
  name: 'Models',
  components: {
    SqrInputText,
    SqrButton,
    SqrErrorBanner,
    SqrErrorText,
    InvoiceLinesForm,
    TrashIcon,
    PlusIcon
},
  computed: {
    ...mapState('account', ['id', 'account', 'loading', 'loadError']),
    models() {
      return this.account?.models ?? [];
    }
  },
  data() {
    return {
      adding: false,
      addError: null,
      removingById: {}
    };
  },
  mounted() {
    this.$a().logEvent('models');
    this.load();
  },
  methods: {
    ...mapActions('account', ['load']),
    async modelAdd() {
      try {
        this.adding = true;
        this.addError = null;
        const id = nanourl(8);
        const model = { id, name: '', lines: [] };
        this.$db()
          .collection('accounts')
          .doc(this.id)
          .update({
            models: firebase.firestore.FieldValue.arrayUnion(model)
          });
      } catch (error) {
        this.addError = error;
      } finally {
        this.adding = false;
      }
    },
    async modelFieldSet(modelIndex, field, value) {
      try {
        const modelsBefore = this.account?.models;
        if (!modelsBefore) return;
        const models = set(lensPath([modelIndex, field]), value, modelsBefore);
        this.$db()
          .collection('accounts')
          .doc(this.id)
          .update({ models });
      } catch (error) {
        console.log(error);
      }
    },
    async modelRemove(modelIndex) {
      try {
        this.removingById[modelIndex] = true;
        const models = remove(modelIndex, 1, this.account.models);
        await this.$db()
          .collection('accounts')
          .doc(this.id)
          .update({ models });
      } catch (error) {
        console.log(error);
      } finally {
        delete this.removingById[modelIndex];
      }
    },
    async lineAdd(modelIndex) {
      try {
        const id = nanourl(8);
        const value = {
          id,
          tarif: '590',
          // code: '',
          // minutes: '',
          // quantity: '',
          // hourly: ''
          vatRate: this.account.vatRate
        };
        const modelsBefore = this.account.models;
        const modelBefore = modelsBefore[modelIndex];
        const model = {
          ...modelBefore,
          lines: [...(modelBefore.lines ?? []), value]
        };
        const models = set(lensIndex(modelIndex), model, modelsBefore);
        await this.$db()
          .collection('accounts')
          .doc(this.id)
          .update({ models });
      } catch (error) {
        console.log(error);
      }
    },
    async lineFieldSet({ modelIndex, index, field, value, data }) {
      try {
        const fromModels = this.account?.models;
        const model = fromModels[modelIndex];
        const origins = model?.lines;
        const origin = origins?.[index];
        if (!origin) return;

        const line = { ...origin, ...data };
        if (field) {
          this.$set(line, field, value);
          invoiceLineComputeByField(line, field, value);
        }
        const path = [modelIndex, 'lines', index];
        const models = set(lensPath(path), line, fromModels);
        await this.$db()
          .collection('accounts')
          .doc(this.id)
          .update({ models });
      } catch (error) {
        console.log(error);
      }
    },
    async lineRemove({ modelIndex, index }) {
      try {
        const modelsBefore = this.account?.models;
        const modelBefore = modelsBefore[modelIndex];
        const line = modelBefore?.lines?.[index];
        if (!line) return;
        const model = {
          ...modelBefore,
          lines: remove(index, 1, modelBefore.lines)
        };
        const models = set(lensIndex(modelIndex), model, modelsBefore);
        await this.$db()
          .collection('accounts')
          .doc(this.id)
          .update({ models });
      } catch (error) {
        console.log(error);
      }
    }
  }
};
</script>
